import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { getAllInventory, IInventory } from "../../services/inventoryApi";
import {
  AuctionBuyer,
  AuctionFinanceResponse,
  AuctionPackage,
  AuctionWin,
  liveAuctionsApi,
} from "../../services/liveAuctionsApi";
import {
  buildAuctionReport,
  BuyerReportRow,
  csvEscape,
  DailyReportRow,
  ReportFilters,
  sortBuyerRows,
} from "./auctionReportUtils";
import { formatMoney as formatAmount } from "../../services/currency";

const input =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const primary =
  "rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50";

const chartPalette = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

const formatMoney = (cents = 0) => formatAmount(cents / 100);

const toLocalYmd = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const addDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const parseLocalYmd = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatDisplayDate = (ymd: string) => {
  if (!ymd) return "";
  return parseLocalYmd(ymd).toLocaleDateString("en-US");
};

const startOfWeek = (date: Date) => {
  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(date, offset);
};

const endOfWeek = (date: Date) => addDays(startOfWeek(date), 6);

const startOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const presetRange = (preset: string) => {
  const today = new Date();
  switch (preset) {
    case "today":
      return { startDate: toLocalYmd(today), endDate: toLocalYmd(today) };
    case "yesterday": {
      const d = addDays(today, -1);
      return { startDate: toLocalYmd(d), endDate: toLocalYmd(d) };
    }
    case "7d":
      return {
        startDate: toLocalYmd(addDays(today, -6)),
        endDate: toLocalYmd(today),
      };
    case "30d":
      return {
        startDate: toLocalYmd(addDays(today, -29)),
        endDate: toLocalYmd(today),
      };
    case "week":
      return {
        startDate: toLocalYmd(startOfWeek(today)),
        endDate: toLocalYmd(endOfWeek(today)),
      };
    case "month":
      return {
        startDate: toLocalYmd(startOfMonth(today)),
        endDate: toLocalYmd(today),
      };
    default:
      return {
        startDate: toLocalYmd(addDays(today, -29)),
        endDate: toLocalYmd(today),
      };
  }
};

const startParam = (ymd: string) => `${ymd}T00:00:00.000`;
const endParam = (ymd: string) => `${ymd}T23:59:59.999`;

const MetricCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string | number;
  helper?: string;
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    {helper ? <div className="mt-1 text-xs text-slate-500">{helper}</div> : null}
  </div>
);

const SectionCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-bold">{title}</h2>
        {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const calendarDayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function DateRangeModal({
  open,
  startDate,
  endDate,
  onClose,
  onApply,
}: {
  open: boolean;
  startDate: string;
  endDate: string;
  onClose: () => void;
  onApply: (next: { startDate: string; endDate: string }) => void;
}) {
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const [monthCursor, setMonthCursor] = useState(() => parseLocalYmd(startDate));

  useEffect(() => {
    if (!open) return;
    setDraftStart(startDate);
    setDraftEnd(endDate);
    setMonthCursor(parseLocalYmd(startDate));
  }, [open, startDate, endDate]);

  const buildMonthCells = (monthDate: Date) => {
    const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const firstWeekday = (first.getDay() + 6) % 7;
    const firstCell = addDays(first, -firstWeekday);
    return Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
  };

  const isInRange = (ymd: string) =>
    draftStart && draftEnd && ymd >= draftStart && ymd <= draftEnd;

  const pickDate = (ymd: string) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(ymd);
      setDraftEnd("");
      return;
    }
    if (ymd < draftStart) {
      setDraftEnd(draftStart);
      setDraftStart(ymd);
      return;
    }
    setDraftEnd(ymd);
  };

  const renderMonth = (baseDate: Date) => {
    const cells = buildMonthCells(baseDate);
    const currentMonth = baseDate.getMonth();
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 text-center font-semibold text-slate-800">
          {baseDate.toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-slate-400">
          {calendarDayNames.map((day) => (
            <div key={day} className="py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((date) => {
            const ymd = toLocalYmd(date);
            const sameMonth = date.getMonth() === currentMonth;
            const isStart = ymd === draftStart;
            const isEnd = ymd === draftEnd;
            const inRange = isInRange(ymd);
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => pickDate(ymd)}
                className={`h-10 rounded-lg text-sm transition ${
                  isStart || isEnd
                    ? "bg-blue-600 font-bold text-white"
                    : inRange
                      ? "bg-blue-100 text-blue-900"
                      : sameMonth
                        ? "text-slate-700 hover:bg-slate-100"
                        : "text-slate-300 hover:bg-slate-50"
                }`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const nextMonth = new Date(
    monthCursor.getFullYear(),
    monthCursor.getMonth() + 1,
    1,
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-slate-950/40 p-4">
        <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold">Choose date range</h2>
              <p className="text-sm text-slate-500">
                Select a start date, then an end date. Dates in between are highlighted.
              </p>
            </div>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={onClose}
            >
              Close
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-bold uppercase text-slate-500">Start date</div>
              <div className="mt-1 text-lg font-bold text-slate-900">
                {draftStart ? formatDisplayDate(draftStart) : "Select start date"}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-bold uppercase text-slate-500">End date</div>
              <div className="mt-1 text-lg font-bold text-slate-900">
                {draftEnd ? formatDisplayDate(draftEnd) : "Select end date"}
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={() =>
                setMonthCursor(
                  new Date(
                    monthCursor.getFullYear(),
                    monthCursor.getMonth() - 1,
                    1,
                  ),
                )
              }
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={() =>
                setMonthCursor(
                  new Date(
                    monthCursor.getFullYear(),
                    monthCursor.getMonth() + 1,
                    1,
                  ),
                )
              }
            >
              Next
            </button>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {renderMonth(monthCursor)}
            {renderMonth(nextMonth)}
          </div>

          <div className="mt-6 flex flex-wrap justify-between gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
              onClick={() => {
                setDraftStart(startDate);
                setDraftEnd(endDate);
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className={primary}
              disabled={!draftStart || !draftEnd}
              onClick={() => {
                onApply({ startDate: draftStart, endDate: draftEnd });
                onClose();
              }}
            >
              Apply range
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function ReportsPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const defaultRange = presetRange("30d");
  const [inventory, setInventory] = useState<IInventory[]>([]);
  const [buyers, setBuyers] = useState<AuctionBuyer[]>([]);
  const [buyerInput, setBuyerInput] = useState("");
  const [selectedBuyerId, setSelectedBuyerId] = useState("");
  const [detailBuyerId, setDetailBuyerId] = useState("");
  const [datePreset, setDatePreset] = useState("30d");
  const [filters, setFilters] = useState<ReportFilters>(defaultRange);
  const [isDateModalOpen, setDateModalOpen] = useState(false);
  const [report, setReport] = useState<ReturnType<typeof buildAuctionReport> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState<
    "revenue" | "profit" | "margin" | "wins" | "shipping"
  >("revenue");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const loadBase = async () => {
      try {
        const [buyerRows, inventoryRows] = await Promise.all([
          liveAuctionsApi.buyers(""),
          getAllInventory().then((response) => response.data.items),
        ]);
        setBuyers(buyerRows);
        setInventory(inventoryRows);
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || "Failed to load reports.");
      }
    };
    void loadBase();
  }, []);

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);
        setError("");
        const finance = await liveAuctionsApi.finance({
          start: startParam(filters.startDate),
          end: endParam(filters.endDate),
          ...(filters.buyerId ? { buyerId: filters.buyerId } : {}),
        });
        const sessionIds = Array.from(
          new Set(finance.wins.map((win) => String(win.auctionSessionId))),
        );
        let sessionWins: AuctionWin[] = [];
        let sessionPackages: AuctionPackage[] = [];
        if (sessionIds.length) {
          const details = await Promise.all(
            sessionIds.map((sessionId) => liveAuctionsApi.session(sessionId)),
          );
          sessionWins = details.flatMap((detail) => detail.wins);
          sessionPackages = details.flatMap((detail) => detail.packages);
        }
        const nextReport = buildAuctionReport({
          wins: sessionWins,
          packages: sessionPackages,
          inventory,
          buyers,
          filters,
        });
        if (!finance.wins.length && sessionWins.length === 0) {
          setReport(
            buildAuctionReport({
              wins: [],
              packages: [],
              inventory,
              buyers,
              filters,
            }),
          );
        } else {
          setReport(nextReport);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || "Failed to load reports.");
      } finally {
        setLoading(false);
      }
    };
    if (!inventory.length && !buyers.length) return;
    void loadReport();
  }, [buyers, filters, inventory]);

  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    setFilters((current) => ({ ...current, ...presetRange(preset) }));
  };

  const sortedBuyerRows = useMemo(
    () => sortBuyerRows(report?.buyerRows || [], sortKey, sortDirection),
    [report?.buyerRows, sortDirection, sortKey],
  );

  const selectedBuyer =
    buyers.find((buyer) => String(buyer._id) === String(detailBuyerId || selectedBuyerId)) ||
    null;

  const detailRows = useMemo(() => {
    if (!report) return [];
    const targetBuyerId = detailBuyerId || selectedBuyerId;
    return targetBuyerId
      ? report.detailRows.filter((row) => row.buyerId === targetBuyerId)
      : report.detailRows;
  }, [detailBuyerId, report, selectedBuyerId]);

  const dailyChartData = useMemo(
    () =>
      (report?.dailyRows || []).map((row) => ({
        date: row.dateKey.slice(5),
        revenue: row.salesCents / 100,
        profit: row.profitCents / 100,
      })),
    [report?.dailyRows],
  );

  const expenseChartData = useMemo(
    () =>
      (report?.breakdown || [])
        .filter((row) => row.valueCents > 0)
        .map((row) => ({
          name: row.name,
          value: row.valueCents / 100,
        })),
    [report?.breakdown],
  );

  const buyerChartData = useMemo(
    () =>
      (report?.topBuyerProfitRows || []).map((row) => ({
        username: row.username,
        profit: row.netProfitCents / 100,
      })),
    [report?.topBuyerProfitRows],
  );

  const exportCsv = () => {
    if (!detailRows.length) return;
    const lines = [
      [
        "Date",
        "Username",
        "Item",
        "Quantity",
        "Winning Price",
        "Revenue",
        "Item Cost",
        "Platform Fee",
        "Package",
        "Packaging Allocation",
        "Shipping Allocation",
        "Shipping Recovery",
        "Net Profit",
        "Margin %",
        "Reconciliation",
      ].join(","),
      ...detailRows.map((row) =>
        [
          csvEscape(row.dateKey),
          csvEscape(row.username),
          csvEscape(row.itemName),
          row.quantity,
          row.winningPriceCents / 100,
          row.revenueCents / 100,
          row.itemCostCents / 100,
          row.platformFeeCents / 100,
          csvEscape(row.packageLabel || ""),
          row.packagingAllocatedCents / 100,
          row.shippingAllocatedCents / 100,
          row.shippingRecoveryAllocatedCents / 100,
          row.netProfitCents / 100,
          row.marginPercent,
          csvEscape(row.packageKnown ? row.packageStatus || "Reconciled" : "Awaiting reconciliation"),
        ].join(","),
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "auction-reports.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const setBuyerFilter = (value: string) => {
    setBuyerInput(value);
    const matched = buyers.find(
      (buyer) => buyer.username.toLowerCase() === value.trim().toLowerCase(),
    );
    setSelectedBuyerId(matched?._id || "");
    setDetailBuyerId(matched?._id || "");
    setFilters((current) => ({
      ...current,
      buyerId: matched?._id || undefined,
    }));
  };

  const clearBuyerFilter = () => {
    setBuyerInput("");
    setSelectedBuyerId("");
    setDetailBuyerId("");
    setFilters((current) => ({ ...current, buyerId: undefined }));
  };

  const toggleSort = (
    key: "revenue" | "profit" | "margin" | "wins" | "shipping",
  ) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDirection("desc");
  };

  const Wrapper = embedded ? "div" : "main";

  return (
    <Wrapper
      className={
        embedded
          ? "mt-6"
          : "min-h-[calc(100vh-56px)] bg-slate-50 px-4 py-8 md:px-8"
      }
    >
      <div className={embedded ? "" : "mx-auto max-w-7xl"}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            {!embedded ? (
              <p className="text-sm font-semibold text-blue-600">TikTok selling</p>
            ) : null}
            <h1 className={embedded ? "text-2xl font-bold" : "text-3xl font-bold"}>
              Reports
            </h1>
            <p className="text-sm text-slate-500">
              Auction revenue, costs, profit, buyers, and reconciliation-aware reporting.
            </p>
          </div>
          <button className={primary} disabled={!detailRows.length} onClick={exportCsv}>
            Export CSV
          </button>
        </div>

        <SectionCard title="Filters" subtitle="Date and buyer filters apply to every metric, chart, and table on this page.">
          <div className="flex flex-wrap gap-2">
            {[
              ["today", "Today"],
              ["yesterday", "Yesterday"],
              ["7d", "Last 7 Days"],
              ["30d", "Last 30 Days"],
              ["week", "This Week"],
              ["month", "This Month"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`rounded-full border px-4 py-2 text-sm font-semibold ${datePreset === value ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-600"}`}
                onClick={() => applyPreset(value)}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              className={`rounded-full border px-4 py-2 text-sm font-semibold ${datePreset === "custom" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-600"}`}
              onClick={() => setDatePreset("custom")}
            >
              Custom
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-[180px_180px_minmax(260px,1fr)_auto]">
            <label className="text-sm font-semibold md:col-span-2">
              Date range
              <button
                type="button"
                className={`${input} mt-1 flex items-center justify-between text-left`}
                onClick={() => setDateModalOpen(true)}
              >
                <span>
                  {formatDisplayDate(filters.startDate)} →{" "}
                  {formatDisplayDate(filters.endDate)}
                </span>
                <span className="text-slate-400">📅</span>
              </button>
            </label>
            <label className="text-sm font-semibold">
              Buyer / Username
              <input
                list="report-buyers"
                className={`${input} mt-1`}
                placeholder="All Buyers"
                value={buyerInput}
                onChange={(event) => setBuyerInput(event.target.value)}
                onBlur={() => setBuyerFilter(buyerInput)}
              />
              <datalist id="report-buyers">
                {buyers.map((buyer) => (
                  <option key={buyer._id} value={buyer.username} />
                ))}
              </datalist>
            </label>
            <div className="flex items-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                onClick={() => setBuyerFilter(buyerInput)}
              >
                Apply buyer
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                onClick={clearBuyerFilter}
              >
                All Buyers
              </button>
            </div>
          </div>
        </SectionCard>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
            Loading report...
          </div>
        ) : error ? (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-8 text-sm text-red-700 shadow-sm">
            {error}
          </div>
        ) : !report ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
            No report data yet.
          </div>
        ) : (
          <>
            <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <MetricCard
                label="Revenue"
                value={formatMoney(report.summary.totalRevenueCents)}
                helper={`${report.summary.totalWins} wins`}
              />
              <MetricCard
                label="Expenses"
                value={formatMoney(report.summary.totalExpensesCents)}
                helper={`Item + fees + shipping + packaging`}
              />
              <MetricCard
                label="Net Profit"
                value={formatMoney(report.summary.netProfitCents)}
                helper={`Gross profit ${formatMoney(report.summary.grossProfitCents)}`}
              />
              <MetricCard
                label="Margin %"
                value={`${report.summary.marginPercent.toFixed(1)}%`}
                helper="Net profit / revenue"
              />
              <MetricCard
                label="Shipping Recovery"
                value={formatMoney(report.summary.shippingRecoveryCents)}
                helper="Buyer-paid shipping + platform credit"
              />
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <MetricCard
                label="Buyers"
                value={report.summary.totalBuyers}
                helper={selectedBuyer ? selectedBuyer.username : "All buyers"}
              />
              <MetricCard
                label="Avg Win Price"
                value={formatMoney(report.summary.averageWinningPriceCents)}
              />
              <MetricCard
                label="Avg Revenue / Buyer"
                value={formatMoney(report.summary.averageRevenuePerBuyerCents)}
              />
              <MetricCard
                label="Profit / Win"
                value={formatMoney(report.summary.profitPerWinCents)}
              />
              <MetricCard
                label="Profit / Buyer"
                value={formatMoney(report.summary.profitPerBuyerCents)}
              />
            </div>

            {report.summary.unreconciledWinUnits > 0 ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {report.summary.unreconciledWinLines} win lines /{" "}
                {report.summary.unreconciledWinUnits} units are awaiting reconciliation.
                Profit shown includes only known shipping and packaging expenses.
              </div>
            ) : null}

            <div className="mt-6 grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
              <SectionCard title="Profit / Loss Breakdown">
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="min-w-full text-left text-sm">
                    <tbody className="divide-y">
                      {[
                        ["Revenue", report.summary.totalRevenueCents],
                        ["Shipping recovery", report.summary.shippingRecoveryCents],
                        ["Item cost", -report.summary.itemCostCents],
                        ["TikTok fees", -report.summary.platformFeesCents],
                        ["Shipping", -report.summary.shippingCostCents],
                        ["Packaging", -report.summary.packagingCostCents],
                        ["Total expenses", -report.summary.totalExpensesCents],
                        ["Net profit", report.summary.netProfitCents],
                      ].map(([label, cents]) => (
                        <tr key={String(label)} className="bg-white">
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {label}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                            {formatMoney(Number(cents))}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-700">
                          Margin
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {report.summary.marginPercent.toFixed(1)}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <SectionCard title="Cost Summary">
                <div className="grid gap-3 sm:grid-cols-2">
                  <MetricCard label="Item Cost" value={formatMoney(report.summary.itemCostCents)} />
                  <MetricCard label="Platform Fees" value={formatMoney(report.summary.platformFeesCents)} />
                  <MetricCard label="Shipping Cost" value={formatMoney(report.summary.shippingCostCents)} />
                  <MetricCard label="Packaging Cost" value={formatMoney(report.summary.packagingCostCents)} />
                </div>
              </SectionCard>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-3">
              <SectionCard
                title="Revenue vs Profit Over Time"
                subtitle="Daily sales and net profit for the current filter."
              >
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value: number) => formatMoney(Math.round(value * 100))} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke={chartPalette[0]} strokeWidth={2} />
                      <Line type="monotone" dataKey="profit" stroke={chartPalette[1]} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              <SectionCard
                title="Expense Breakdown"
                subtitle="Relative share of tracked costs."
              >
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={expenseChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label
                      >
                        {expenseChartData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={chartPalette[index % chartPalette.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatAmount(Number(value))} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>

              <SectionCard
                title="Profit by Buyer"
                subtitle="Top buyers by net profit."
              >
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={buyerChartData} layout="vertical" margin={{ left: 12, right: 12 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="username" width={110} />
                      <Tooltip formatter={(value: number) => formatAmount(Number(value))} />
                      <Bar dataKey="profit" fill={chartPalette[1]} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>

            <SectionCard
              title="Daily Report"
              subtitle="Performance by day for the active filters."
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Sales</th>
                      <th className="px-4 py-3 text-right">Wins</th>
                      <th className="px-4 py-3 text-right">Buyers</th>
                      <th className="px-4 py-3 text-right">Expenses</th>
                      <th className="px-4 py-3 text-right">Profit</th>
                      <th className="px-4 py-3 text-right">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {report.dailyRows.length ? (
                      report.dailyRows.map((row: DailyReportRow) => (
                        <tr key={row.dateKey}>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {row.dateKey}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.salesCents)}
                          </td>
                          <td className="px-4 py-3 text-right">{row.wins}</td>
                          <td className="px-4 py-3 text-right">{row.buyers}</td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.expensesCents)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatMoney(row.profitCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {row.marginPercent.toFixed(1)}%
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                          No daily activity for this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard
              title="Buyer Report"
              subtitle="Click a buyer row to open their detailed wins."
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Username</th>
                      <th className="px-4 py-3 text-right">
                        <button type="button" onClick={() => toggleSort("wins")}>
                          Wins
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right">
                        <button type="button" onClick={() => toggleSort("revenue")}>
                          Revenue
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right">Item Cost</th>
                      <th className="px-4 py-3 text-right">Fees</th>
                      <th className="px-4 py-3 text-right">
                        <button type="button" onClick={() => toggleSort("shipping")}>
                          Shipping
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right">Packaging</th>
                      <th className="px-4 py-3 text-right">Total Cost</th>
                      <th className="px-4 py-3 text-right">
                        <button type="button" onClick={() => toggleSort("profit")}>
                          Net Profit
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right">
                        <button type="button" onClick={() => toggleSort("margin")}>
                          Margin %
                        </button>
                      </th>
                      <th className="px-4 py-3 text-right">Packages</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {sortedBuyerRows.length ? (
                      sortedBuyerRows.map((row: BuyerReportRow) => (
                        <tr
                          key={row.buyerId}
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => {
                            setDetailBuyerId(row.buyerId);
                            setBuyerInput(row.username);
                            setSelectedBuyerId(row.buyerId);
                            setFilters((current) => ({
                              ...current,
                              buyerId: row.buyerId,
                            }));
                          }}
                        >
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {row.username}
                          </td>
                          <td className="px-4 py-3 text-right">{row.wins}</td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.revenueCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.itemCostCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.platformFeesCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.shippingCostCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.packagingCostCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.totalCostCents)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatMoney(row.netProfitCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {row.marginPercent.toFixed(1)}%
                          </td>
                          <td className="px-4 py-3 text-right">{row.packageCount}</td>
                          <td className="px-4 py-3">{row.reconciliationStatus}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                          No buyers matched this report filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard
              title={selectedBuyer ? `Buyer Detail: ${selectedBuyer.username}` : "Buyer Detail"}
              subtitle={
                selectedBuyer
                  ? "Detailed wins for the selected buyer."
                  : "Select a buyer to inspect individual wins."
              }
            >
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Username</th>
                      <th className="px-4 py-3">Item</th>
                      <th className="px-4 py-3 text-right">Winning Price</th>
                      <th className="px-4 py-3 text-right">Item Cost</th>
                      <th className="px-4 py-3 text-right">Platform Fee</th>
                      <th className="px-4 py-3">Package</th>
                      <th className="px-4 py-3 text-right">Shipping Alloc.</th>
                      <th className="px-4 py-3 text-right">Packaging Alloc.</th>
                      <th className="px-4 py-3 text-right">Profit</th>
                      <th className="px-4 py-3 text-right">Margin</th>
                      <th className="px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y bg-white">
                    {detailRows.length ? (
                      detailRows.map((row) => (
                        <tr key={row.winId}>
                          <td className="px-4 py-3">{row.dateKey}</td>
                          <td className="px-4 py-3">{row.username}</td>
                          <td className="px-4 py-3">
                            {row.itemName} × {row.quantity}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.revenueCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.itemCostCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.platformFeeCents)}
                          </td>
                          <td className="px-4 py-3">
                            {row.packageLabel || "Awaiting reconciliation"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.shippingAllocatedCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatMoney(row.packagingAllocatedCents)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            {formatMoney(row.netProfitCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {row.marginPercent.toFixed(1)}%
                          </td>
                          <td className="px-4 py-3">
                            {row.packageKnown
                              ? row.packageStatus || "Reconciled"
                              : "Awaiting reconciliation"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                          {selectedBuyer
                            ? "No wins found for this buyer in the selected range."
                            : "Select a buyer to view detailed wins."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </>
        )}
      </div>
      <DateRangeModal
        open={isDateModalOpen}
        startDate={filters.startDate}
        endDate={filters.endDate}
        onClose={() => setDateModalOpen(false)}
        onApply={({ startDate, endDate }) => {
          setDatePreset("custom");
          setFilters((current) => ({
            ...current,
            startDate,
            endDate,
          }));
        }}
      />
    </Wrapper>
  );
}
